import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../config/axiosInstance";
import { extractList } from "../utils/api";
import { formatDate, statusLabel, statusTone } from "../utils/format";
import { useCompany } from "../hooks/useCompany";
import { useAuth } from "../contexts/AuthContext";
import StatCard from "../components/StatCard";
import { FiCheckCircle, FiLayers, FiEye, FiMousePointer, FiInfo, FiArrowRight, FiTarget } from "react-icons/fi";

const DashboardPage = () => {
  const { user } = useAuth();
  const { company, loading } = useCompany();
  const [ads, setAds] = useState([]);
  const [loadingAds, setLoadingAds] = useState(false);
  const [pendingCompanies, setPendingCompanies] = useState([]);
  const [loadingPendingCompanies, setLoadingPendingCompanies] = useState(false);
  const [companyStats, setCompanyStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
  });

  const isAdmin = user?.role === "ADMIN";

  useEffect(() => {
    const fetchAds = async () => {
      if (!company?.id) return;
      setLoadingAds(true);
      try {
        const response = await axiosInstance.get("/ads", {
          params: { itemsPerPage: -1, companyId: company.id },
        });
        const list = extractList(response);
        setAds(list);
      } catch (error) {
        setAds([]);
      } finally {
        setLoadingAds(false);
      }
    };

    fetchAds();
  }, [company?.id]);

  useEffect(() => {
    const fetchPendingCompanies = async () => {
      if (!isAdmin) return;

      setLoadingPendingCompanies(true);
      try {
        const response = await axiosInstance.get("/companies", {
          params: { itemsPerPage: -1 },
        });

        const companiesList = extractList(response);

        const list = companiesList
          .filter((item) => item?.statusVerificacao === "PENDENTE")
          .sort(
            (a, b) =>
              new Date(b?.createdAt || 0).getTime() -
              new Date(a?.createdAt || 0).getTime()
          );

        setCompanyStats({
          total: companiesList.length,
          approved: companiesList.filter(
            (item) => item?.statusVerificacao === "APROVADO"
          ).length,
          pending: list.length,
          rejected: companiesList.filter(
            (item) => item?.statusVerificacao === "REJEITADO"
          ).length,
        });

        setPendingCompanies(list);
      } catch (error) {
        setCompanyStats({ total: 0, approved: 0, pending: 0, rejected: 0 });
        setPendingCompanies([]);
      } finally {
        setLoadingPendingCompanies(false);
      }
    };

    fetchPendingCompanies();
  }, [isAdmin]);

  const totalViews = ads.reduce((sum, ad) => sum + (ad.viewsCount || 0), 0);
  const totalClicks = ads.reduce((sum, ad) => sum + (ad.clicksCount || 0), 0);
  const approvedAds = ads.filter((ad) => ad.status === "APROVADO").length;

  return (
    <div className="fade-in page-stack">
      <div className="page-header">
        <div>
          <h2>{isAdmin ? "Painel do admin" : "Painel do anunciante"}</h2>
          <p>
            {isAdmin
              ? "Acompanhe empresas pendentes e gerencie aprovacoes da plataforma."
              : "Acompanhe o desempenho dos seus anuncios e o status de verificacao da empresa."}
          </p>
        </div>
        {!isAdmin ? (
          <Link className="button button--primary" to="/anuncios/novo">
            Novo anúncio
          </Link>
        ) : null}
      </div>

      <div className="stat-grid">
        {isAdmin ? (
          <>
            <StatCard
              title="Empresas pendentes"
              value={companyStats.pending}
              note="Aguardando aprovacao"
              tone="sunset"
              icon={FiInfo}
            />
            <StatCard
              title="Empresas aprovadas"
              value={companyStats.approved}
              note="Com verificacao concluida"
              tone="mint"
              icon={FiCheckCircle}
            />
            <StatCard
              title="Total de empresas"
              value={companyStats.total}
              note="Cadastradas na plataforma"
              tone="sky"
              icon={FiLayers}
            />
            <StatCard
              title="Empresas rejeitadas"
              value={companyStats.rejected}
              note="Exigem nova analise"
              tone="violet"
              icon={FiTarget}
            />
          </>
        ) : (
          <>
            <StatCard
              title="Anúncios ativos"
              value={approvedAds}
              note="Aprovados"
              tone="sunset"
              icon={FiCheckCircle}
            />
            <StatCard
              title="Total de anuncios"
              value={ads.length}
              note="Criados"
              tone="sky"
              icon={FiLayers}
            />
            <StatCard
              title="Visualizacoes"
              value={totalViews}
              note="Ultimos 30 dias"
              tone="mint"
              icon={FiEye}
            />
            <StatCard
              title="Cliques"
              value={totalClicks}
              note="Interacoes"
              tone="violet"
              icon={FiMousePointer}
            />
          </>
        )}
      </div>

      {isAdmin ? (
        <div className="dashboard-overview">
          <div className="overview-card overview-card--status">
            <div className="overview-card__header">
              <div>
                <span className="eyebrow">Moderacao</span>
                <h3 className="card-title">Aprovações de empresas</h3>
                <p className="card-subtitle">Acompanhe pendencias de verificacao.</p>
              </div>
              <span className="badge" data-tone="warning">
                {pendingCompanies.length} pendentes
              </span>
            </div>

            <div className="overview-card__mini">
              <div className="overview-mini">
                <span>Total pendentes</span>
                <strong>{pendingCompanies.length}</strong>
              </div>
              <div className="overview-mini">
                <span>Ultima atualizacao</span>
                <strong>{loadingPendingCompanies ? "Carregando" : "Agora"}</strong>
              </div>
              <div className="overview-mini">
                <span>Acesso rapido</span>
                <strong>
                  <Link to="/empresas">Ver empresas</Link>
                </strong>
              </div>
            </div>
          </div>

          <div className="overview-card overview-card--steps">
            <div className="overview-steps__header">
              <span className="eyebrow">Pendentes</span>
              <h3 className="card-title">Últimas empresas para aprovação</h3>
              <p className="card-subtitle">Revise e aprove para liberar anúncios.</p>
            </div>

            {loadingPendingCompanies ? (
              <div className="muted">Carregando empresas pendentes...</div>
            ) : pendingCompanies.length === 0 ? (
              <div className="muted">Nenhuma empresa pendente no momento.</div>
            ) : (
              <ol className="overview-steps">
                {pendingCompanies.slice(0, 5).map((pendingCompany, index) => (
                  <li key={pendingCompany.id} className="overview-step--admin">
                    <span className="overview-step__badge !text-white">{index + 1}</span>
                    <div className="overview-step__content">
                      <div className="overview-step__top">
                        <strong>
                          {pendingCompany.nomeFantasia ||
                            pendingCompany.razaoSocial ||
                            "Empresa sem nome"}
                        </strong>
                      </div>
                      <span>
                        {pendingCompany.emailCorporativo ||
                          pendingCompany.email ||
                          "Sem e-mail"}
                      </span>
                    </div>
                    <Link
                      className="button button--ghost overview-step__action"
                      to={`/empresas/${pendingCompany.id}`}
                    >
                      Revisar
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      ) : loading ? (
        <div className="card card--outlined">
          Carregando dados da empresa...
        </div>
      ) : !company ? (
        <div
          className="card card--accent fade-in"
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "2rem",
            background: "linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)",
            color: "white",
            borderRadius: "1rem",
            boxShadow: "0 10px 25px -5px rgba(76, 175, 80, 0.4)",
            border: "none",
            gap: "2rem",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1 1 min-content", minWidth: "250px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
              <div style={{ backgroundColor: "rgba(255, 255, 255, 0.2)", padding: "0.5rem", borderRadius: "0.5rem", display: "flex" }}>
                <FiInfo size={24} color="white" />
              </div>
              <h3 className="card-title" style={{ color: "white", margin: 0, fontSize: "1.25rem" }}>
                Ação Necessária: Finalize seu cadastro
              </h3>
            </div>
            <p className="card-subtitle" style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "1rem", lineHeight: 1.6, margin: 0 }}>
              Você está a um passo de alcançar mais clientes! Para começar a criar anúncios, preencha os dados básicos da sua empresa e envie os documentos de verificação. Nosso processo é rápido e seguro.
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", alignSelf: "center" }}>
            <Link
              className="button"
              to="/empresa"
              style={{
                backgroundColor: "white",
                color: "#388E3C",
                fontWeight: "600",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.875rem 1.5rem",
                borderRadius: "0.5rem",
                border: "none",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                transition: "transform 0.2s, box-shadow 0.2s",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 10px 15px -3px rgba(0, 0, 0, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
              }}
            >
              Completar Dados
              <FiArrowRight />
            </Link>
          </div>
        </div>
      ) : (
        <div className="dashboard-overview">
          <div className="overview-card overview-card--status">
            <div className="overview-card__header">
              <div>
                <span className="eyebrow">Empresa</span>
                <h3 className="card-title">
                  {company.nomeFantasia || company.razaoSocial}
                </h3>
                <p className="card-subtitle">Resumo da operação</p>
              </div>
              <span
                className="badge"
                data-tone={statusTone(company.statusVerificacao)}
              >
                {statusLabel(company.statusVerificacao)}
              </span>
            </div>

            <div className="overview-card__usage">
              <div className="overview-card__count">
                <span>Uso do limite</span>
                <strong>
                  {ads.length} / {company.limiteAnúncios || 0}
                </strong>
                <small>anúncios usados</small>
              </div>
              <div className="overview-card__progress">
                <div className="overview-card__progress-bar">
                  <div
                    className="overview-card__progress-fill"
                    style={{
                      width: `${Math.min(
                        (ads.length /
                          Math.max(company.limiteAnúncios || 0, 1)) *
                          100,
                        100
                      )}%`,
                    }}
                  />
                </div>
                <div className="overview-card__progress-meta">
                  <span>Aprovados</span>
                  <strong>{approvedAds}</strong>
                </div>
              </div>
            </div>

            <div className="overview-card__mini">
              <div className="overview-mini">
                <span>Total</span>
                <strong>{ads.length}</strong>
              </div>
              <div className="overview-mini">
                <span>Aprovados</span>
                <strong>{approvedAds}</strong>
              </div>
              <div className="overview-mini">
                <span>Limite</span>
                <strong>{company.limiteAnúncios || 0}</strong>
              </div>
            </div>
          </div>

          <div className="overview-card overview-card--steps">
            <div className="overview-steps__header">
              <span className="eyebrow">Checklist</span>
              <h3 className="card-title">Próximos passos</h3>
              <p className="card-subtitle">
                Complete para liberar todas as funções.
              </p>
            </div>
            <ol className="overview-steps">
              <li>
                <span className="overview-step__badge !text-white">1</span>
                <div>
                  <strong>Enviar documentos</strong>
                  <span>na aba Minha Empresa.</span>
                </div>
              </li>
              <li>
                <span className="overview-step__badge  !text-white">2</span>
                <div>
                  <strong>Cadastrar produtos e regiões</strong>
                  <span>para segmentar campanhas.</span>
                </div>
              </li>
              <li>
                <span className="overview-step__badge  !text-white">3</span>
                <div>
                  <strong>Criar seu primeiro anúncio</strong>
                  <span>com público bem definido.</span>
                </div>
              </li>
            </ol>
          </div>
        </div>
      )}

      <div className="section-card" style={{ padding: "1.5rem", borderRadius: "1rem", backgroundColor: "white", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)", border: "1px solid var(--color-border, #e5e7eb)" }}>
        <div className="page-header" style={{ marginBottom: "1.5rem", paddingBottom: "1rem", borderBottom: "1px solid #f3f4f6" }}>
          <div>
            <h2 style={{ fontSize: "1.25rem", fontWeight: "700", color: "#111827", margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <FiTarget className="text-primary" style={{ color: "var(--color-primary-main, #bb2649)" }} /> Anúncios Recentes
            </h2>
            <p style={{ margin: "0.5rem 0 0 0", color: "#6b7280", fontSize: "0.875rem" }}>Últimos anúncios criados pela sua empresa.</p>
          </div>
          <Link className="button button--ghost" to="/anuncios" style={{ color: "var(--color-primary-main, #bb2649)", fontWeight: "600", fontSize: "0.875rem" }}>
            Ver todos
          </Link>
        </div>
        
        {loadingAds ? (
          <div style={{ padding: "2rem", textAlign: "center", color: "#9ca3af", fontSize: "0.875rem" }}>Carregando anúncios...</div>
        ) : ads.length === 0 ? (
          <div className="flex flex-col justify-center items-center" style={{ padding: "3rem 1rem", textAlign: "center", backgroundColor: "#f9fafb", borderRadius: "0.5rem", border: "1px dashed #d1d5db" }}>
            <FiLayers size={32} color="#9ca3af" style={{ marginBottom: "1rem" }} />
            <p style={{ margin: 0, color: "#6b7280", fontWeight: "500" }}>Nenhum anúncio criado até agora.</p>
            <Link to="/anuncios/novo" style={{ display: "inline-block", marginTop: "1rem", color: "var(--color-primary-main, #bb2649)", fontWeight: "600", textDecoration: "none" }}>Criar meu primeiro anúncio &rarr;</Link>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {ads.slice(0, 5).map((ad) => (
              <div 
                key={ad.id} 
                style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center", 
                  padding: "1rem 1.25rem", 
                  borderRadius: "0.75rem", 
                  border: "1px solid #f3f4f6",
                  backgroundColor: "#ffffff",
                  transition: "all 0.2s ease-in-out",
                  cursor: "pointer"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-primary-light, #fdf2f8)";
                  e.currentTarget.style.backgroundColor = "#fdfafb";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(187, 38, 73, 0.05)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#f3f4f6";
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <div style={{ width: "44px", height: "44px", flexShrink: 0, borderRadius: "0.5rem", backgroundColor: "var(--color-primary-light, #fdf2f8)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-primary-main, #bb2649)" }}>
                    <FiLayers size={20} />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                    <h4 style={{ margin: 0, color: "#1f2933", fontWeight: "600", fontSize: "0.95rem" }}>{ad.title}</h4>
                    <div>
                      <span className="badge" data-tone={statusTone(ad.status)} style={{ fontSize: "0.7rem", padding: "0.15rem 0.5rem" }}>
                        {statusLabel(ad.status)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div style={{ display: "flex", gap: "2rem" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.25rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", color: "#6b7280", fontSize: "0.8rem", fontWeight: "500" }}>
                      <FiEye size={14} /> Visitas
                    </div>
                    <strong style={{ color: "#111827", fontSize: "0.95rem" }}>{ad.viewsCount || 0}</strong>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.25rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", color: "#6b7280", fontSize: "0.8rem", fontWeight: "500" }}>
                      <FiMousePointer size={14} /> Cliques
                    </div>
                    <strong style={{ color: "#111827", fontSize: "0.95rem" }}>{ad.clicksCount || 0}</strong>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
